import React, { useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { AbTestListItem } from './../ab-test-list-item/AbTestListItem';
import { AbTest } from '../../../models/AbTest';
import { Tab, Tabs } from '../../../../../../../../../../react-common/components/tabs/Tabs';
import { IAbTestActions } from '../../../models/AbTestActions';
import { AbTestType, AbTestTabTypeLabel, reverseTabMap, tabMap } from '../../../models/AbTestType';
import useCheckouts from '../../ab-test-modal/hooks/useCheckouts';
import useExtensions from '../../../../../../../../../../react-common/hooks/useExtensions';
import AbTestCard from '../AbTestCard/AbTestCard';
import useExtensionGroups from '@app/react-common/hooks/useExtensionGroups';
import useThemes from '../../ab-test-modal/hooks/useThemes';
import useProductPages from '../../ab-test-modal/hooks/useProductPages';
import useSmartCarts from '../../ab-test-modal/hooks/useSmartCarts';
import useCheckoutProfiles from '../../ab-test-modal/hooks/useCheckoutProfiles';
import { AbTestTypeCategory } from '../../../models/AbTestCreationType';

interface AbTestsListProps {
  draftAbTests: AbTest[];
  concludedAbTests: AbTest[];
  runningAbTests: AbTest[];
  abTestActions: IAbTestActions;
  runningAbTestCheckoutIds: number[];
  enablePredictedSegmentAbTestWinner?: boolean | undefined;
  freeShippingThreshold?: number | null;
  bigSpenderThreshold?: number | null;
}

export const ListAbTests = ({
  draftAbTests,
  runningAbTests,
  concludedAbTests,
  abTestActions,
  runningAbTestCheckoutIds,
  enablePredictedSegmentAbTestWinner,
  freeShippingThreshold,
  bigSpenderThreshold,
}: AbTestsListProps) => {
  const { checkouts } = useCheckouts();
  const { extensions } = useExtensions();
  const { extensionGroups } = useExtensionGroups();

  const isPdp = [draftAbTests, runningAbTests, concludedAbTests]
    .flat()
    .some((test) => test?.testType === AbTestTypeCategory.PRODUCT_PAGE);

  const { productPages } = useProductPages(isPdp);

  const isCrossSite = [draftAbTests, runningAbTests, concludedAbTests]
    .flat()
    .some((test) => test?.testType === AbTestTypeCategory.CROSS_SITE);

  const { themes } = useThemes(isCrossSite);

  const isSmartCart = [draftAbTests, runningAbTests, concludedAbTests]
    .flat()
    .some((test) => test?.testType === AbTestTypeCategory.SMART_CART);

  const { smartCarts } = useSmartCarts(isSmartCart);

  const hasCheckoutProfileTests = [draftAbTests, runningAbTests, concludedAbTests]
    .flat()
    .some((test) => test?.testType === AbTestTypeCategory.CHECKOUT_PROFILE);

  const { checkoutProfiles } = useCheckoutProfiles(hasCheckoutProfileTests);

  const location = useLocation();
  const history = useHistory();
  const [activeTab, setActiveTab] = React.useState<string>();
  const actionsToTabMap = {
    [AbTestTabTypeLabel.DRAFTS]: abTestActions.fetchDraftTests,
    [AbTestTabTypeLabel.RUNNING]: abTestActions.fetchRunningTests,
    [AbTestTabTypeLabel.CONCLUDED_TESTS]: abTestActions.fetchConcludedTests,
  };
  const onTabChange = (tab: string) => {
    const action = actionsToTabMap[tab];
    if (action) {
      action();
    }
  };
  const updateUrl = (tab: string) => {
    history.replace(`/?tab=${reverseTabMap[tab]}`);
  };
  const handleTabClick = (tab: string) => {
    onTabChange(tab);
    updateUrl(tab);
  };
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabMap[tabParam]) {
      const tabLabel = tabMap[tabParam];
      onTabChange(tabLabel);
      setActiveTab(tabLabel);
    } else {
      onTabChange(AbTestTabTypeLabel.DRAFTS);
      setActiveTab(AbTestTabTypeLabel.DRAFTS);
      updateUrl(AbTestTabTypeLabel.DRAFTS);
    }
  }, []);
  return (
    <div className="ab-tests-list">
      <Tabs onTabChange={handleTabClick} activeTab={activeTab} setActiveTab={setActiveTab}>
        <Tab label={AbTestTabTypeLabel.DRAFTS} showTab={true}>
          {enablePredictedSegmentAbTestWinner ? (
            <div
              className={`cards-container-drafts ${draftAbTests?.length === 1 ? 'cards-container-drafts--single' : ''}`}>
              {draftAbTests?.length > 0
                ? draftAbTests.map((abTest) => {
                    return (
                      <AbTestCard
                        key={abTest.id}
                        abTest={abTest}
                        checkouts={checkouts}
                        extensions={extensions}
                        extensionGroups={extensionGroups}
                        runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                        setActiveTab={setActiveTab}
                        abTestActions={abTestActions}
                        freeShippingThreshold={freeShippingThreshold}
                        bigSpenderThreshold={bigSpenderThreshold}
                        themes={themes}
                        productPages={productPages}
                        smartCarts={smartCarts}
                        checkoutProfiles={checkoutProfiles}
                      />
                    );
                  })
                : noAbTests(AbTestType.DRAFT)}
            </div>
          ) : draftAbTests?.length > 0 ? (
            draftAbTests.map((abTest) => {
              return (
                <AbTestListItem
                  abTest={abTest}
                  key={abTest.id}
                  abTestActions={abTestActions}
                  checkouts={checkouts}
                  extensions={extensions}
                  extensionGroups={extensionGroups}
                  runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                  setActiveTab={setActiveTab}
                  themes={themes}
                  smartCarts={smartCarts}
                />
              );
            })
          ) : (
            noAbTests(AbTestType.DRAFT)
          )}
        </Tab>
        <Tab label={AbTestTabTypeLabel.RUNNING} showTab={runningAbTests.length > 0}>
          <div className="cards-container-running">
            {enablePredictedSegmentAbTestWinner
              ? runningAbTests?.length > 0
                ? runningAbTests.map((abTest) => {
                    return (
                      <AbTestCard
                        key={abTest.id}
                        abTest={abTest}
                        checkouts={checkouts}
                        extensions={extensions}
                        extensionGroups={extensionGroups}
                        runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                        setActiveTab={setActiveTab}
                        abTestActions={abTestActions}
                        freeShippingThreshold={freeShippingThreshold}
                        bigSpenderThreshold={bigSpenderThreshold}
                        themes={themes}
                        productPages={productPages}
                        smartCarts={smartCarts}
                        checkoutProfiles={checkoutProfiles}
                      />
                    );
                  })
                : noAbTests(AbTestType.RUNNING)
              : runningAbTests?.length > 0
                ? runningAbTests.map((abTest) => {
                    return (
                      <AbTestListItem
                        abTest={abTest}
                        key={abTest.id}
                        abTestActions={abTestActions}
                        checkouts={checkouts}
                        extensions={extensions}
                        extensionGroups={extensionGroups}
                        runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                        setActiveTab={setActiveTab}
                        themes={themes}
                      />
                    );
                  })
                : noAbTests(AbTestType.RUNNING)}
          </div>
        </Tab>
        <Tab label={AbTestTabTypeLabel.CONCLUDED_TESTS} showTab={true}>
          {enablePredictedSegmentAbTestWinner
            ? concludedAbTests?.length > 0
              ? concludedAbTests.map((abTest) => {
                  return (
                    <AbTestCard
                      key={abTest.id}
                      abTest={abTest}
                      checkouts={checkouts}
                      extensions={extensions}
                      extensionGroups={extensionGroups}
                      runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                      setActiveTab={setActiveTab}
                      abTestActions={abTestActions}
                      freeShippingThreshold={freeShippingThreshold}
                      bigSpenderThreshold={bigSpenderThreshold}
                      themes={themes}
                      productPages={productPages}
                      checkoutProfiles={checkoutProfiles}
                    />
                  );
                })
              : noAbTests(AbTestType.CONCLUDED)
            : concludedAbTests?.length > 0
              ? concludedAbTests.map((abTest) => {
                  return (
                    <AbTestListItem
                      abTest={abTest}
                      key={abTest.id}
                      abTestActions={abTestActions}
                      checkouts={checkouts}
                      extensions={extensions}
                      extensionGroups={extensionGroups}
                      runningAbTestCheckoutIds={runningAbTestCheckoutIds}
                      setActiveTab={setActiveTab}
                      themes={themes}
                    />
                  );
                })
              : noAbTests(AbTestType.CONCLUDED)}
        </Tab>
      </Tabs>
    </div>
  );
};

function noAbTests(type: AbTestType) {
  let text = '';
  if (type === AbTestType.DRAFT) {
    text = 'There are no draft A/B tests.';
  }
  if (type === AbTestType.RUNNING) {
    text = 'There are no A/B tests currently running.';
  }
  if (type === AbTestType.CONCLUDED) {
    text = 'There are no concluded A/B tests.';
  }
  return (
    <div className="ab-tests-view__not-found">
      <p>{text}</p>
    </div>
  );
}
